/**
 * Copyright (c) 2020
 *
 * Contains the representation of a ProductOption
 *
 * @summary productOption class definition
 * @author Julien <julien.donnadieu@yooda.com>
 *
 * Created at     : 2020-06-08 10:21:56
 * Last modified  : 2021-08-17 15:34:17
 */

import Product from '@/models/customersManager/entity/Product'
import OptionTypeEnumType from '@/models/customersManager/enum/OptionTypeEnumType'

class ProductOption {

    /**
     * @type id Integer
     */
    #id

    /**
     * @type name String
     */
    #name

    /**
     * @type handle String
     */
    #handle

    /**
     * @type value String
     */
    #value

    /**
     * @type type String
     */
    #type

    /**
     * @type product null|Product
     */
    #product = null

    /**
     * @param rawProductOption null|Object
     */
    constructor(rawProductOption = null) {
        if (rawProductOption !== null) {
            const { id, name, handle, value, type, product } = rawProductOption
            this.id = id
            this.name = name
            this.value = value
            this.handle = handle
            this.type = type
            this.product = product
        }
    }

    /**
     * @returns Integer
     */
    get id () {
        return this.#id
    }

    /**
     * @param id String
     * @returns void
     */
    set id (id) {
        this.#id = parseInt(id)
    }

    /**
     * @returns String
     */
    get name () {
        return this.#name
    }

    /**
     * @param name String
     * @returns void
     */
    set name (name) {
        this.#name = name
    }

    /**
     * @returns String
     */
    get handle () {
        return this.#handle
    }

    /**
     * @param handle String
     * @returns void
     */
    set handle (handle) {
        this.#handle = handle
    }

    /**
     * @returns Integer|Float|String
     */
    get value () {
        if (this.#type === OptionTypeEnumType.INTEGER) {
            return parseInt(this.#value)
        } else if (this.#type === OptionTypeEnumType.DECIMAL) {
            return parseFloat(this.#value)
        } else {
            return this.#value
        }
    }

    /**
     * @param value String
     * @returns void
     */
    set value (value) {
        this.#value = value
    }

    /**
     * @returns String
     */
    get type () {
        return this.#type
    }

    /**
     * @param type String
     * @returns void
     */
    set type (type) {
        if (!Object.values(OptionTypeEnumType).includes(type)) {
            throw new Error('optionType is not defined')
        }
        this.#type = type
    }

    /**
     * @returns null|Product
     */
    get product () {
        return this.#product
    }

    /**
     * @param rawProduct null|String
     * @returns void
     */
    set product (rawProduct) {
        if (rawProduct !== null && typeof rawProduct !== 'undefined') {
            this.#product = new Product(rawProduct)
        } else {
            this.#product = null
        }
    }
}

export default ProductOption
